import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Textarea,
} from '@mlm/ui';
import {
  announcementImageUrl,
  createAnnouncement,
  deleteAnnouncement,
  listAnnouncements,
  updateAnnouncement,
  uploadAnnouncementImage,
  type Announcement,
} from '../lib/api';

/** Status label for the list row, derived from publish + expiry timestamps. */
function statusOf(a: Announcement): { label: string; tone: string } {
  if (!a.publishedAt) return { label: 'Draft', tone: 'bg-muted text-muted-foreground' };
  if (a.expiresAt && new Date(a.expiresAt) <= new Date())
    return { label: 'Expired', tone: 'bg-muted text-muted-foreground' };
  if (new Date(a.publishedAt) > new Date())
    return { label: 'Scheduled', tone: 'bg-amber-100 text-amber-700' };
  return { label: 'Published', tone: 'bg-green-100 text-green-700' };
}

/**
 * Announcements CMS. Admin-authored notices broadcast to all members, shown on
 * the member Dashboard card. Each has a title, content, and an optional banner
 * image; publishing is a timestamp (draft = unpublished, optional expiry).
 */
export function AnnouncementsPage() {
  const query = useQuery({ queryKey: ['admin-announcements'], queryFn: listAnnouncements });
  const items = query.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Announcements</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Post notices that appear on every member's Dashboard. Add a title and content, optionally
          a banner image, then publish. Unpublished posts stay as drafts; set an expiry to
          auto-hide.
        </p>
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>Posts</CardTitle>
          <CreateButton />
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No announcements yet.</p>
          ) : (
            <ul className="divide-y">
              {items.map((a) => (
                <AnnouncementRow key={a.id} announcement={a} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function CreateButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>+ New announcement</Button>
      {open && <FormModal onClose={() => setOpen(false)} />}
    </>
  );
}

function AnnouncementRow({ announcement }: { announcement: Announcement }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const remove = useMutation({
    mutationFn: () => deleteAnnouncement(announcement.id),
    meta: { successMessage: 'Announcement deleted.' },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-announcements'] }),
  });
  const status = statusOf(announcement);
  return (
    <li className="flex items-center gap-3 py-3">
      <div className="h-12 w-20 shrink-0 overflow-hidden rounded bg-muted">
        {announcement.imageKey ? (
          <img
            src={announcementImageUrl(announcement.id)}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : null}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium">{announcement.title}</span>
          <span className={`text-xs px-1.5 py-0.5 rounded ${status.tone}`}>{status.label}</span>
        </div>
        <div className="text-xs text-muted-foreground truncate">{announcement.body}</div>
      </div>
      <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
        Edit
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          if (confirm(`Delete announcement "${announcement.title}"?`)) remove.mutate();
        }}
      >
        Delete
      </Button>
      {editing && <FormModal announcement={announcement} onClose={() => setEditing(false)} />}
    </li>
  );
}

type Channel = 'dashboard' | 'bell' | 'both';
const CHANNEL_OPTIONS: Array<{ value: Channel; label: string; hint: string }> = [
  {
    value: 'dashboard',
    label: 'Dashboard widget',
    hint: 'Shows on the member Dashboard card only.',
  },
  { value: 'bell', label: 'Notification bell', hint: "Pushes to every active member's bell only." },
  { value: 'both', label: 'Both', hint: 'Dashboard card and a bell notification.' },
];

/** datetime-local <input> wants 'YYYY-MM-DDTHH:mm' in local time. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function FormModal({
  announcement,
  onClose,
}: {
  announcement?: Announcement;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(announcement?.title ?? '');
  const [body, setBody] = useState(announcement?.body ?? '');
  const [imageKey, setImageKey] = useState<string | null>(announcement?.imageKey ?? null);
  const [published, setPublished] = useState(Boolean(announcement?.publishedAt));
  const [expiresAt, setExpiresAt] = useState<string>(toLocalInput(announcement?.expiresAt ?? null));
  const initialChannel: Channel = announcement
    ? announcement.deliverBell && announcement.deliverDashboard
      ? 'both'
      : announcement.deliverBell
        ? 'bell'
        : 'dashboard'
    : 'dashboard';
  const [channel, setChannel] = useState<Channel>(initialChannel);
  const fileRef = useRef<HTMLInputElement>(null);

  const upload = useMutation({ mutationFn: uploadAnnouncementImage });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-announcements'] });
  const create = useMutation({
    mutationFn: createAnnouncement,
    meta: { successMessage: 'Announcement saved.' },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });
  const update = useMutation({
    mutationFn: (patch: Parameters<typeof updateAnnouncement>[1]) =>
      updateAnnouncement(announcement!.id, patch),
    meta: { successMessage: 'Announcement saved.' },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    // Keep the original published timestamp if it was already published; stamp
    // "now" the first time it's published. Unchecking returns it to a draft.
    const publishedAt = published ? (announcement?.publishedAt ?? new Date().toISOString()) : null;
    const deliverDashboard = channel === 'dashboard' || channel === 'both';
    const deliverBell = channel === 'bell' || channel === 'both';
    // Publishing with the bell channel on broadcasts to every active member —
    // confirm once. Already-fanned-out posts won't re-notify (server guard).
    if (publishedAt && deliverBell && !announcement?.fannedOutAt) {
      if (!confirm('This will send a notification to every active member. Continue?')) return;
    }
    const payload = {
      title: title.trim(),
      body: body.trim(),
      imageKey,
      deliverDashboard,
      deliverBell,
      publishedAt,
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
    };
    if (announcement) update.mutate(payload);
    else create.mutate(payload);
  }

  const pending = create.isPending || update.isPending;
  const err =
    (create.error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error ??
    (update.error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-lg border bg-card p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold mb-3">
          {announcement ? 'Edit announcement' : 'New announcement'}
        </h2>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Content</label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} required />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Banner image (optional)</label>
            <div className="flex items-center gap-2">
              {imageKey && <span className="text-xs text-green-600">✓ uploaded</span>}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const res = await upload.mutateAsync(file);
                  setImageKey(res.key);
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
              >
                {upload.isPending ? 'Uploading…' : imageKey ? 'Replace' : 'Upload image'}
              </Button>
              {imageKey && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setImageKey(null)}>
                  Remove
                </Button>
              )}
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Expires (optional)</label>
            <Input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">Where to deliver</label>
            <div className="flex flex-col gap-1.5">
              {CHANNEL_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name="channel"
                    className="mt-1"
                    checked={channel === opt.value}
                    onChange={() => setChannel(opt.value)}
                  />
                  <span>
                    <span className="font-medium">{opt.label}</span>
                    <span className="block text-xs text-muted-foreground">{opt.hint}</span>
                  </span>
                </label>
              ))}
            </div>
            {announcement?.fannedOutAt && (
              <p className="text-xs text-muted-foreground">
                ✓ Delivered to member bells on {new Date(announcement.fannedOutAt).toLocaleString()}
                . Editing won't re-notify.
              </p>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={published} onCheckedChange={(v) => setPublished(v === true)} />
            Published (visible to members)
          </label>
          {err && <p className="text-sm text-destructive">{err}</p>}
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={pending}>
              {pending ? 'Saving…' : announcement ? 'Save' : 'Create'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
